import { RefObject, useEffect, useRef } from 'react';

// Distance (px) the section top can sit from the viewport top before we consider it "already snapped".
const AT_TOP_THRESHOLD = 8;

// Roughly matches the vertical-stepper's open/close spring settle time. Waiting for the collapsing and
// expanding sections to finish animating before scrolling means we scroll to a settled position in one
// smooth motion, rather than chasing a height that's still changing (which reads as a stutter).
const SPRING_SETTLE_MS = 350;

export const useScrollIntoView = (enabled: boolean | undefined = true): { ref: RefObject<HTMLDivElement> } => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled) return;

    // Defer until the section's layout has settled (the previous section collapses and this one
    // expands), then measure. Previously we skipped scrolling whenever the anchor was anywhere in the
    // viewport, which left partially-visible sections not aligned to the top. Now we scroll unless the
    // section is already at the top, so a newly-active section reliably lands at the top of the viewport.
    const timeout = setTimeout(() => {
      const element = ref.current;
      if (!element) return;
      if (Math.abs(element.getBoundingClientRect().top) > AT_TOP_THRESHOLD) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, SPRING_SETTLE_MS);

    return () => clearTimeout(timeout);
  }, [enabled]);

  return { ref };
};
